// Naive-date helpers for the analytics surface. URL filters stay as
// `YYYY-MM-DD` strings, interpreted in the dashboard-selected timezone.

import type { Moment } from 'moment';
import momentTz from 'moment-timezone';

import type { SupportedTimeZone } from '@/constants/date';
import { TIMEZONE_MAP } from '@/utils/date';

const dailyTrendMaxDays = 14;
const weeklyTrendMonths = 2;

type AnalyticsTrendBucket = 'day' | 'week' | 'month';

interface AnalyticsTrendBucketParams {
  // Only the bucket starts are sent. Buckets are contiguous and half-open, so the
  // backend derives each end from the next start (and the last from the `end`
  // window) — no ends array is needed.
  trend_bucket_starts: string[];
}

export interface AnalyticsTrendBucketPlan {
  /** Request query params: the bucket windows the backend groups rows into. */
  params: AnalyticsTrendBucketParams;
  /**
   * Maps each bucket's UTC start (echoed back by the backend as a trend row's
   * `date`) to its `YYYY-MM-DD` chart label. The backend is timezone-agnostic
   * and only knows the windows, so the UI re-attaches the human label here.
   */
  labelByStart: Record<string, string>;
  /**
   * UTC starts of buckets shorter than one full grain unit, i.e. cut by the end of the
   * range. Charts mark their labels with `*` (see `formatBucketLabel`).
   */
  partialStarts: Set<string>;
  /** The grain the buckets were built with (which can differ from the backend's `trend_bucket`). */
  bucket: AnalyticsTrendBucket;
  /** The exclusive end of the range in UTC: the next local midnight after the last day. */
  rangeEndExclusive: string;
}

const formatUtcSecond = (m: Moment): string => `${m.clone().utc().format('YYYY-MM-DDTHH:mm:ss')}Z`;

const parseNaiveDateInTimezone = (date: string, timeZone: SupportedTimeZone) =>
  momentTz.tz(date, 'YYYY-MM-DD', true, TIMEZONE_MAP[timeZone]);

// timeZone is required (no default): the caller passes the dashboard-wide value
// from the store so analytics never forks its own default.
const todayISO = (timeZone: SupportedTimeZone): string =>
  momentTz.tz(TIMEZONE_MAP[timeZone]).format('YYYY-MM-DD');

/**
 * Returns the ISO date `days` days back from today, inclusive of today.
 * `daysAgoISO(7, tz)` → start of a 7-day window ending today (today is day 1).
 */
const daysAgoISO = (days: number, timeZone: SupportedTimeZone): string =>
  momentTz
    .tz(TIMEZONE_MAP[timeZone])
    .subtract(days - 1, 'days')
    .format('YYYY-MM-DD');

export const getDefaultDateRange = (
  days: number,
  timeZone: SupportedTimeZone,
): { start: string; end: string } => ({
  start: daysAgoISO(days, timeZone),
  end: todayISO(timeZone),
});

const getTrendBucketForDateRange = (
  start: string,
  end: string,
  timeZone: SupportedTimeZone,
): AnalyticsTrendBucket => {
  const startMoment = parseNaiveDateInTimezone(start, timeZone).startOf('day');
  const endMoment = parseNaiveDateInTimezone(end, timeZone).startOf('day');
  if (!startMoment.isValid() || !endMoment.isValid() || endMoment.isBefore(startMoment)) {
    return 'day';
  }

  const inclusiveDays = endMoment.diff(startMoment, 'days') + 1;
  if (inclusiveDays <= dailyTrendMaxDays) {
    return 'day';
  }
  if (!endMoment.isAfter(startMoment.clone().add(weeklyTrendMonths, 'months'))) {
    return 'week';
  }
  return 'month';
};

/**
 * Builds the explicit trend-bucket windows to send, plus a map to relabel the
 * response. Each bucket spans one grain unit (day/week/month) in the selected
 * timezone; `starts`/`ends` are the UTC bounds, and `labelByStart` remembers the
 * `YYYY-MM-DD` label for each start so the response can be relabeled locally.
 */
export const buildTrendBucketPlan = (
  start: string | undefined,
  end: string | undefined,
  timeZone: SupportedTimeZone,
): AnalyticsTrendBucketPlan | undefined => {
  if (!start || !end) return undefined;

  const bucket = getTrendBucketForDateRange(start, end, timeZone);
  const rangeStart = parseNaiveDateInTimezone(start, timeZone).startOf('day');
  const rangeEndExclusive = parseNaiveDateInTimezone(end, timeZone).add(1, 'day').startOf('day');

  if (
    !rangeStart.isValid() ||
    !rangeEndExclusive.isValid() ||
    !rangeStart.isBefore(rangeEndExclusive)
  ) {
    return undefined;
  }

  const unit = bucket === 'month' ? 'month' : bucket === 'week' ? 'week' : 'day';
  const starts: string[] = [];
  const labelByStart: Record<string, string> = {};
  const partialStarts = new Set<string>();

  let cursor = rangeStart.clone();
  while (cursor.isBefore(rangeEndExclusive)) {
    const next = momentTz.min(cursor.clone().add(1, unit), rangeEndExclusive);
    const startUtc = formatUtcSecond(cursor);
    starts.push(startUtc);
    labelByStart[startUtc] = cursor.format('YYYY-MM-DD');
    if (next.isBefore(cursor.clone().add(1, unit))) partialStarts.add(startUtc);
    cursor = next;
  }

  return {
    params: { trend_bucket_starts: starts },
    labelByStart,
    partialStarts,
    bucket,
    rangeEndExclusive: formatUtcSecond(rangeEndExclusive),
  };
};

/** Marks a partial bucket's label with `*`. `start` is the bucket's key in `partialStarts`. */
export const formatBucketLabel = (
  start: string,
  label: string,
  partialStarts: Set<string>,
): string => (partialStarts.has(start) ? `${label}*` : label);

interface TrendPoint {
  date: string;
}

const relabelTrendPoints = <P extends TrendPoint>(
  points: P[] | undefined,
  labelByStart: Record<string, string>,
): P[] => (points ?? []).map(p => ({ ...p, date: labelByStart[p.date] ?? p.date }));

/**
 * Rewrites each trend row's `date` (the backend echoes the bucket's UTC start)
 * back to its `YYYY-MM-DD` chart label using the plan's labelByStart map, so the
 * charts keep receiving stable, sortable date labels.
 */
export function applyTrendBucketLabels<T>(
  response: T,
  keys: readonly (keyof T)[],
  labelByStart: Record<string, string>,
): T {
  const out = { ...response };
  for (const key of keys) {
    out[key] = relabelTrendPoints(
      response[key] as unknown as TrendPoint[] | undefined,
      labelByStart,
    ) as unknown as T[keyof T];
  }
  return out;
}
